import { Injectable, inject } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ScrollService {
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly router = inject(Router);

  /**
   * Flag to temporarily disable auto-scroll on next navigation
   * Resets to false after navigation completes
   */
  private skipNextScroll = false;

  /**
   * Flag to permanently disable auto-scroll until manually re-enabled
   */
  private scrollDisabled = false;

  constructor() {
    this.setupScrollListener();
  }

  /**
   * Skip scroll to top on the next navigation only
   * Automatically resets after navigation
   */
  skipScrollOnce(): void {
    this.skipNextScroll = true;
  }

  /**
   * Disable all automatic scrolling until enableScroll() is called
   */
  disableScroll(): void {
    this.scrollDisabled = true;
  }

  /**
   * Re-enable automatic scrolling
   */
  enableScroll(): void {
    this.scrollDisabled = false;
  }

  /**
   * Check if scroll should be performed
   */
  shouldScroll(): boolean {
    if (this.scrollDisabled) {
      return false;
    }
    if (this.skipNextScroll) {
      this.skipNextScroll = false;
      return false;
    }
    return true;
  }

  /**
   * Scroll to top of the page (0, 0)
   */
  scrollToTop(): void {
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  /**
   * Scroll to top with delay (useful after content load)
   */
  scrollToTopDelayed(delay: number = 100): void {
    setTimeout(() => this.scrollToTop(), delay);
  }

  /**
   * Scroll to specific element by ID
   */
  scrollToElement(elementId: string, delay: number = 0): void {
    setTimeout(() => {
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, delay);
  }

  /**
   * Auto-scroll to top when fragment is 'top'
   */
  private setupScrollListener(): void {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const fragment = this.router.parseUrl(this.router.url).fragment;
        if (fragment === 'top' && this.shouldScroll()) {
          this.scrollToTopDelayed(50);
        }
      });
  }

  /**
   * Update URL without triggering scroll to top
   * Uses history.replaceState to avoid Angular router scroll behavior
   */
  updateUrlWithoutScroll(url: string): void {
    window.history.replaceState({}, '', url);
  }

  /**
   * Navigate using router but skip scroll on this navigation
   */
  navigateWithoutScroll(commands: any[], extras?: any): Promise<boolean> {
    this.skipScrollOnce();
    return this.router.navigate(commands, extras);
  }
}
